import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, Check, Clock, ArrowRight, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicShell, Loading } from "@/components/shared";
import api from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function Playbooks() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["playbooks"], queryFn: async () => (await api.get("/playbooks")).data });
  if (isLoading) return <PublicShell><Loading /></PublicShell>;
  const products = data?.products || [];
  const bundle = products.find((p) => p.is_bundle);
  const items = products.filter((p) => !p.is_bundle);

  return (
    <PublicShell>
      <section className="relative overflow-hidden bg-navy grain">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navysurface/90" />
        <div className="relative mx-auto max-w-4xl px-5 py-20 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-emerald">The Playbooks</p>
          <h1 className="mt-5 font-heading text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl">
            The written products. Each one solves one selling problem.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
            Short, working documents — calculators, scripts and one-pagers you can use this week. Buy the one you need, or take all eight as the bundle.
          </p>
        </div>
      </section>

      {/* Bundle — the obvious choice */}
      {bundle && (
        <section className="mx-auto max-w-5xl px-5 pt-14 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border-2 border-emerald bg-emerald/[0.04] p-8 lg:p-10" data-testid="playbook-bundle">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald px-3 py-1 text-xs font-bold text-white">Best value</span>
            <div className="mt-4 lg:flex lg:items-center lg:justify-between lg:gap-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-navy">{bundle.title}</h2>
                <p className="mt-2 max-w-xl text-slate-600">{bundle.description}</p>
                <p className="mt-3 text-sm text-slate-500">All eight playbooks · normally {inr(499 * 8)} bought separately</p>
              </div>
              <div className="mt-5 flex-shrink-0 text-center lg:mt-0">
                <p className="font-heading text-4xl font-bold text-navy">{inr(bundle.price)}</p>
                <Button onClick={() => navigate(`/playbooks/${bundle.slug}`)} className="mt-3 h-11 w-full rounded-md bg-emerald px-8 font-semibold text-white hover:bg-emerald-700" data-testid="bundle-buy-btn">
                  {bundle.owned ? "You own this" : "Get the bundle"}
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Individual grid */}
      <section className="mx-auto max-w-5xl px-5 py-14 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <div key={p.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6" data-testid={`playbook-card-${p.slug}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Module {p.module}</span>
                {p.status === "available"
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald/10 px-2 py-0.5 text-[11px] font-bold text-emerald"><Check className="h-3 w-3" /> Available</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700"><Clock className="h-3 w-3" /> {p.available_date || "Coming soon"}</span>}
              </div>
              <h3 className="mt-3 font-heading text-lg font-semibold text-navy">{p.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.description}</p>
              <div className="mt-5 flex items-center justify-between">
                <span className="font-heading text-xl font-bold text-navy">{inr(p.price)}</span>
                {p.owned ? (
                  <Button onClick={() => navigate(`/playbooks/${p.slug}`)} variant="outline" size="sm" className="rounded-md" data-testid={`playbook-open-${p.slug}`}><FileText className="mr-1.5 h-4 w-4" /> Open</Button>
                ) : p.status === "available" ? (
                  <Button onClick={() => navigate(`/playbooks/${p.slug}`)} size="sm" className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid={`playbook-buy-${p.slug}`}>Buy <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>
                ) : (
                  <Button disabled size="sm" variant="ghost" className="rounded-md text-slate-400" data-testid={`playbook-soon-${p.slug}`}><Lock className="mr-1.5 h-3.5 w-3.5" /> Soon</Button>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-10 text-center text-sm text-slate-500" data-testid="playbooks-library-link">
          Want the cheap entry point?{" "}
          <button onClick={() => navigate("/library")} className="font-semibold text-emerald underline-offset-4 hover:underline">The Library</button>
          {" "}— nine complete programs, 63 resources, one payment of ₹499.
        </p>
      </section>
    </PublicShell>
  );
}
